

import * as ENGINE from '@gnsx/genesys.js';

import type { AssetHandler } from '../../assetTypes';
import type { IAssetFile } from '../assetHandler';
import type { DropPayload } from '@/editor/core/dragDropTypes';
import type * as THREE from 'three';

import { nameHelpers } from '@/editor/core';
import { asyncLoadPrefabAsset, ensurePrefabAsset } from '@/editor/core/actions/prefabActions';
import { editorScene, store } from '@/editor/store';
import { applyDefaultOutlinerPlacementIntent, captureDefaultOutlinerPlacementIntent } from '@/editor/store/sceneStore';

export class PrefabHandler implements AssetHandler {
  private actor: ENGINE.Actor | null = null;

  private async addActor(asset: IAssetFile, worldPosition: THREE.Vector3): Promise<ENGINE.Actor | null> {
    const world = editorScene.world;
    if (!world) {
      console.error('No world instance found');
      return null;
    }

    const outlinerPlacement = captureDefaultOutlinerPlacementIntent();

    try {
      await ensurePrefabAsset(asset.path);

      const actors = await ENGINE.WorldCommands.placePrefabs({
        world,
        prefabs: [{
          path: asset.path,
          transform: {
            position: worldPosition,
          },
        }],
      });

      // world.addActors(...actors);
      editorScene.updateOutliner();
      const placedActors = actors.filter((a): a is ENGINE.Actor => a != null);
      const root = editorScene.outliner.rootNode ?? undefined;
      nameHelpers.assignUniqueActorDisplayNames(placedActors, root, { getAssetPathHint: () => asset.path });
      for (const a of placedActors) {
        editorScene.outliner.getNodeById(a.uuid)?.setName(a.name);
      }
      const placedUuids = actors.map(a => a?.uuid).filter((id): id is string => id != null);
      applyDefaultOutlinerPlacementIntent(placedUuids, outlinerPlacement);
      if (actors[0]) {
        editorScene.selectObject(actors[0]);
        requestAnimationFrame(() => {
          editorScene.outliner.scrollToSelected();
        });
      }

      return actors ? actors[0] : null;
    } catch (error) {
      console.error('Failed to load prefab:', error);
      return null;
    }
  }

  async handleSelect(asset: IAssetFile): Promise<void> {
    console.log(`Prefab handler select: ${asset.path}`);
    await ensurePrefabAsset(asset.path, true);
    store.setSelectedAssetItem(asset);
  }

  async handleOpen(asset: IAssetFile): Promise<void> {
    const success = await ensurePrefabAsset(asset.path);
    if (success) {
      const prefabAsset = await asyncLoadPrefabAsset(asset.path);
      editorScene.selectObject(prefabAsset);
    }
  }

  async handleDragEnter(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor = await this.addActor(payload.payload, worldPosition);
  }

  async handleDragOver(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor?.rootComponent.setWorldPosition(worldPosition);
    if (this.actor && this.actor === editorScene.selectedObject?.asActor) {
      editorScene.selectedObject.setDisplayPosition(worldPosition);
    }
  }

  async handleDragCancel(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    editorScene.selectObject(null);
    this.actor?.destroy();
    editorScene.updateOutliner();
  }

  async handleDrop(worldPosition: THREE.Vector3, payload: DropPayload): Promise<void> {
    this.actor = null;
  }

  getTargetObject(): unknown {
    return this.actor;
  }
}